import React, { PropsWithChildren, RefObject, useMemo, useState, JSX } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  ApiFilter,
  ApiGroupedMultipleSelectionFilter,
  ApiMultipleSelectionFilter,
  ApiNumberRangeFilter,
  ApiSingleSelectionFilter
} from '@ibe/api';
import CustomPagination from '../../Components/CustomPagination/CustomPagination';
import PackageUtil from '../../Modules/BuildPackage/Util/PackageUtil';
import { CustomListOptions, ServiceListProps } from './externals';
import { useMount } from 'react-use';
import { Filter } from './Filter';

export interface FilterState {
  isOpen: boolean;
  onChange: (value: boolean) => void;
}

export interface FilterProperties {
  filter: ApiFilter[];
  onFilterChange: (
    filter:
      | ApiMultipleSelectionFilter
      | ApiNumberRangeFilter
      | ApiGroupedMultipleSelectionFilter
      | ApiSingleSelectionFilter
  ) => void;
  state: FilterState;
  onFiltersDiscard: () => void;
}

interface LazyLoadProps {
  height: number;
  children: React.ReactNode;
}

const LazyLoad = ({ height, children }: LazyLoadProps): JSX.Element => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: '300px 0px'
  });

  return (
    <div ref={ref} style={{ minHeight: inView ? undefined : height }}>
      {inView ? children : null}
    </div>
  );
};

const FadeIn = (props: PropsWithChildren<{}>): JSX.Element => {
  const { children } = props;
  const [isMounted, setIsMounted] = useState<boolean>(false);

  useMount(() => {
    setIsMounted(true);
  });

  return <div className={`fade-in${isMounted ? ' fade-in--transition' : ''}`}>{children}</div>;
};

function List<G extends { id: string }>(
  props: ServiceListProps<G> & {
    topContent?: React.ReactNode;
    searchWidgetContainerRef?: RefObject<HTMLDivElement | null>;
    hasContent: boolean;
    itemList: G[];
    noFilters?: boolean;
  }
): JSX.Element {
  const {
    filterProps,
    items,
    paginationProps,
    renderItem,
    selectedItemId,
    additionalContent,
    occupancy,
    useLazyLoading,
    externalTopContent,
    mapConfig,
    noFilters
  } = props;

  const itemList = useMemo(() => {
    return selectedItemId
      ? PackageUtil.getItemsToDisplayWithSelectedAsFirst(selectedItemId, items)
      : items;
  }, [selectedItemId, items]);

  const hasFilters = useMemo(() => !!filterProps?.filter && filterProps.filter.length > 0, [
    filterProps
  ]);

  const hasContent = useMemo(() => hasFilters || !!additionalContent || !!mapConfig, [
    hasFilters,
    additionalContent,
    mapConfig
  ]);

  return (
    <div
      id="service-list-container"
      className={`service-list__container w-100${
        hasContent && !noFilters ? ' w-md-75 pl-md-2' : ''
      }`}
    >
      {!!externalTopContent && externalTopContent}
      {itemList.map((item, idx) =>
        useLazyLoading && itemList.length > 5 && idx > 2 ? (
          <LazyLoad key={JSON.stringify(item)} height={500}>
            <FadeIn>
              <div className="service-list__item mb-3">
                {renderItem(item, idx, occupancy || [])}
              </div>
            </FadeIn>
          </LazyLoad>
        ) : (
          <div key={JSON.stringify(item)} className="service-list__item mb-3">
            {renderItem(item, idx, occupancy || [])}
          </div>
        )
      )}
      {paginationProps && !paginationProps.hidden && items.length > 0 && (
        <>
          {useLazyLoading ? (
            <LazyLoad height={150}>
              <CustomPagination
                amount={paginationProps.amount}
                currentPage={paginationProps.currentPage}
                index={paginationProps.index}
                onAmountChange={paginationProps.onAmountChange}
                onFirst={paginationProps.onFirst}
                onLast={paginationProps.onLast}
                onNext={paginationProps.onNext}
                onPageNumber={paginationProps.onPageNumber}
                onPrev={paginationProps.onPrev}
                totalCount={paginationProps.totalCount}
                showing={paginationProps.showing}
              />
            </LazyLoad>
          ) : (
            <CustomPagination
              amount={paginationProps.amount}
              currentPage={paginationProps.currentPage}
              index={paginationProps.index}
              onAmountChange={paginationProps.onAmountChange}
              onFirst={paginationProps.onFirst}
              onLast={paginationProps.onLast}
              onNext={paginationProps.onNext}
              onPageNumber={paginationProps.onPageNumber}
              onPrev={paginationProps.onPrev}
              totalCount={paginationProps.totalCount}
              showing={paginationProps.showing}
            />
          )}
        </>
      )}
    </div>
  );
}

export function ServiceList<G extends { id: string }>(
  props: ServiceListProps<G> & {
    topContent?: React.ReactNode;
    searchWidgetContainerRef?: RefObject<HTMLDivElement | null>;
    searchWidgetChangedSize?: boolean;
    expectedOffset?: number;
    noFilters?: boolean;
    customListOptions?: CustomListOptions;
  }
): JSX.Element {
  const {
    filterProps,
    items,
    selectedItemId,
    additionalContent,
    mapConfig,
    searchWidgetContainerRef,
    searchWidgetChangedSize,
    expectedOffset,
    ExtendedMapMarkerPopup,
    mapRef,
    noFilters,
    customFilter,
    store,
    customListOptions
  } = props;

  const itemList = useMemo(() => {
    return selectedItemId
      ? PackageUtil.getItemsToDisplayWithSelectedAsFirst(selectedItemId, items)
      : items;
  }, [selectedItemId, items]);

  const hasFilters = useMemo(() => !!filterProps?.filter && filterProps.filter.length > 0, [
    filterProps
  ]);

  const hasContent = useMemo(() => hasFilters || !!additionalContent || !!mapConfig, [
    hasFilters,
    additionalContent,
    mapConfig
  ]);

  const customFilterOnChange = (value: boolean) => {
    if (customFilter?.test && customFilter.serviceListStoreCallback) {
      if (value) {
        store?.setCustomFilter(customFilter.test);
      } else {
        store?.clearCustomFilter();
      }
      customFilter.serviceListStoreCallback();
    }
  };

  const getInitialCustomFilterValue = () => {
    if (store) {
      return store.isCustomFilter;
    } else {
      return false;
    }
  };

  /* eslint-disable */
  return noFilters ? (
    <List {...props} hasContent={hasContent} itemList={itemList} noFilters={noFilters} />
  ) : (
    <Filter
      {...props}
      hasFilters={hasFilters}
      hasContent={hasContent}
      searchWidgetContainerRef={searchWidgetContainerRef}
      searchWidgetChangedSize={searchWidgetChangedSize}
      expectedOffset={expectedOffset}
      ExtendedMapMarkerPopup={ExtendedMapMarkerPopup}
      mapRef={mapRef}
      customFilterElement={
        customFilter?.renderer &&
        customFilter.renderer(getInitialCustomFilterValue(), customFilterOnChange)
      }
      customListOptions={customListOptions}
    >
      <List {...props} hasContent={hasContent} itemList={itemList} />
    </Filter>
  );
  /* eslint-enable */
}
